import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import * as Cesium from 'cesium';
import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs';
import { CameraService } from './camera.service';
import { ConfigurationManagerService } from './configuration-manager.service';
import { getAuthToken } from '../utility/login-info';

export interface GtdIncident {
  eventid: string;
  iyear: number;
  imonth: number;
  iday: number;
  dateStr: string;
  country: string;
  provstate: string;
  city: string;
  latitude: number;
  longitude: number;
  specificity: number;
  vicinity: number;
  location: string;
  summary: string;
  success: boolean;
  suicide: boolean;
  attacktype: string;
  attacktype2?: string;
  targtype: string;
  targsubtype?: string;
  corp?: string;
  target?: string;
  natlty?: string;
  gname: string;
  gsubname?: string;
  motive?: string;
  claimed?: boolean;
  claimmode?: string;
  weaptype: string;
  weapsubtype?: string;
  weapdetail?: string;
  nkill: number;
  nkillus: number;
  nkillter: number;
  nwound: number;
  nwoundus: number;
  property: number;
  ishostkid: boolean;
  nhostkid: number;
  ransom: boolean;
  ransomamt?: string;
  ransompaid?: string;
  hostkidoutcome?: string;
  scite1?: string;
  scite2?: string;
  scite3?: string;
  dbsource?: string;
  color: string;
  icon: string;
  iconUrl: string;
  totalCasualties: number;
}

export interface GtdStats {
  totalIncidents: number;
  totalKilled: number;
  totalWounded: number;
  totalHostages: number;
  filteredIncidents: number;
  filteredKilled: number;
  filteredWounded: number;
  filteredHostages: number;
}

export interface GtdAttackTypeCount {
  name: string;
  color: string;
  icon: string;
  iconUrl: string;
  count: number;
  selected: boolean;
}

export interface GtdStateHotspot {
  state: string;
  count: number;
  totalKilled: number;
  topGroup: string;
  latitude: number;
  longitude: number;
}

export interface GtdGroupCount {
  name: string;
  count: number;
  killed: number;
}

export interface GtdHoverTooltipInfo {
  x: number;
  y: number;
  incident: GtdIncident;
}

export interface GtdFilterState {
  minYear: number;
  maxYear: number;
  selectedAttackTypes: Set<string>;
  selectedGroup: string;
  selectedState: string;
  minCasualties: number;
  searchQuery: string;
  onlyFatal: boolean;
  onlyKidnappings: boolean;
}

// Tactical SVG Vector Glyphs (Military / Defense circular badge aesthetic matching reference)
export const TACTICAL_SVG_ICONS: Record<string, { svgInner: string; color: string; label: string }> = {
  'Bombing/Explosion': {
    color: '#EF4444',
    label: 'Bombing / Explosion',
    svgInner: `
      <!-- Lit Fuse Bomb with Sparks -->
      <circle cx="21" cy="27" r="10" fill="#FFFFFF"/>
      <circle cx="18" cy="24" r="2.2" fill="#0B0F19"/>
      <path d="M24 16 L27 12 Q30 8 35 10" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" fill="none"/>
      <rect x="22" y="15" width="5" height="3" rx="1" fill="#FFFFFF" transform="rotate(-30 24 16)"/>
      <path d="M37 6 L39 4 M40 9 L43 8 M36 12 L39 14" stroke="#EF4444" stroke-width="2" stroke-linecap="round"/>
    `
  },
  'Armed Assault': {
    color: '#F97316',
    label: 'Armed Assault',
    svgInner: `
      <!-- Exact Beretta M9 / Tactical Handgun (Matching User's Reference Image) -->
      <!-- Main Pistol Silhouette Body -->
      <path d="M 10.5 21 C 10.5 19.5, 12 18.5, 13 18 L 13 16 L 14.5 14.5 L 15.5 15.2 L 33 15.2 L 34.5 13.8 L 37 13.8 L 37.5 15.2 L 37.5 19.8 L 33 19.8 L 32 21.5 L 27.5 21.5 C 27.5 25.5, 23 26.5, 21.5 23.5 L 18 34 L 13.5 34 C 13 32, 14 24, 14.2 22.5 C 13.5 22.2, 10.5 22, 10.5 21 Z" fill="#FFFFFF"/>
      
      <!-- Trigger Guard Cutout & Trigger -->
      <path d="M 23 22 L 27 22 C 27 25, 23.5 25.5, 22.5 23 Z" fill="#0B0F19"/>
      <path d="M 24.8 22 Q 24.8 23.5 23.8 24.3" stroke="#FFFFFF" stroke-width="1.3" stroke-linecap="round" fill="none"/>
      
      <!-- Slide Horizontal Detail Line -->
      <line x1="16.5" y1="17.2" x2="34" y2="17.2" stroke="#0B0F19" stroke-width="1.2" stroke-linecap="round"/>
      
      <!-- Safety Pivot Dot -->
      <circle cx="14.2" cy="17.5" r="1.1" fill="#0B0F19"/>
      
      <!-- 3 Grip Serration Lines -->
      <line x1="16.5" y1="26" x2="15.2" y2="31.5" stroke="#0B0F19" stroke-width="1.2" stroke-linecap="round"/>
      <line x1="18.5" y1="26" x2="17.2" y2="31.5" stroke="#0B0F19" stroke-width="1.2" stroke-linecap="round"/>
      <line x1="20.5" y1="26" x2="19.2" y2="31.5" stroke="#0B0F19" stroke-width="1.2" stroke-linecap="round"/>
    `
  },
  'Hostage Taking (Kidnapping)': {
    color: '#A855F7',
    label: 'Kidnapping',
    svgInner: `
      <!-- Interlocking Tactical Handcuffs with Chain -->
      <circle cx="17.5" cy="24" r="5.5" stroke="#FFFFFF" stroke-width="2.4" fill="none"/>
      <circle cx="17.5" cy="24" r="2.2" fill="#FFFFFF"/>
      <circle cx="30.5" cy="24" r="5.5" stroke="#FFFFFF" stroke-width="2.4" fill="none"/>
      <circle cx="30.5" cy="24" r="2.2" fill="#FFFFFF"/>
      <line x1="22.5" y1="22.5" x2="25.5" y2="22.5" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round"/>
      <line x1="22.5" y1="25.5" x2="25.5" y2="25.5" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round"/>
    `
  },
  'Facility/Infrastructure Attack': {
    color: '#06B6D4',
    label: 'Facility Attack',
    svgInner: `
      <!-- Classical Pillar Government / Infrastructure Building -->
      <path d="M13 19 L24 12 L35 19 H13 Z" fill="#FFFFFF"/>
      <rect x="15.5" y="21" width="3" height="8" rx="0.5" fill="#FFFFFF"/>
      <rect x="22.5" y="21" width="3" height="8" rx="0.5" fill="#FFFFFF"/>
      <rect x="29.5" y="21" width="3" height="8" rx="0.5" fill="#FFFFFF"/>
      <rect x="12" y="30" width="24" height="3" rx="0.5" fill="#FFFFFF"/>
    `
  },
  'Assassination': {
    color: '#E11D48',
    label: 'Assassination',
    svgInner: `
      <!-- Sniper Crosshair Reticle -->
      <circle cx="24" cy="24" r="10.5" stroke="#FFFFFF" stroke-width="2.2" fill="none"/>
      <circle cx="24" cy="24" r="3.5" fill="#FFFFFF"/>
      <line x1="24" y1="9" x2="24" y2="16" stroke="#FFFFFF" stroke-width="2.2" stroke-linecap="round"/>
      <line x1="24" y1="32" x2="24" y2="39" stroke="#FFFFFF" stroke-width="2.2" stroke-linecap="round"/>
      <line x1="9" y1="24" x2="16" y2="24" stroke="#FFFFFF" stroke-width="2.2" stroke-linecap="round"/>
      <line x1="32" y1="24" x2="39" y2="24" stroke="#FFFFFF" stroke-width="2.2" stroke-linecap="round"/>
    `
  },
  'Hostage Taking (Barricade Incident)': {
    color: '#EC4899',
    label: 'Barricade Incident',
    svgInner: `
      <!-- Shield / Barricade Defense Barrier -->
      <path d="M24 12 L35 16 V24 C35 30.5, 30.5 34.5, 24 36.5 C17.5 34.5, 13 30.5, 13 24 V16 Z" fill="#FFFFFF"/>
      <path d="M24 16 V32 M16 24 H32" stroke="#0B0F19" stroke-width="2.4" stroke-linecap="round"/>
    `
  },
  'Unarmed Assault': {
    color: '#EAB308',
    label: 'Unarmed Assault',
    svgInner: `
      <!-- Clenched Tactical Combat Glove / Warning Fist -->
      <path d="M24 13 L36 33 H12 Z" fill="#FFFFFF"/>
      <circle cx="24" cy="29.5" r="1.4" fill="#0B0F19"/>
      <line x1="24" y1="19.5" x2="24" y2="26" stroke="#0B0F19" stroke-width="2.5" stroke-linecap="round"/>
    `
  },
  'Hijacking': {
    color: '#3B82F6',
    label: 'Hijacking',
    svgInner: `
      <!-- Tactical Aircraft Silhouette -->
      <path d="M24 11 L26.5 19 L36 23 V26 L26.5 24 V30 L29 32.5 V34.5 L24 33.5 L19 34.5 V32.5 L21.5 30 V24 L12 26 V23 L21.5 19 Z" fill="#FFFFFF"/>
    `
  },
  'Unknown': {
    color: '#94A3B8',
    label: 'Unknown',
    svgInner: `
      <!-- Tactical Question Glyph -->
      <circle cx="24" cy="24" r="11" stroke="#FFFFFF" stroke-width="2" stroke-dasharray="3,3" fill="none"/>
      <path d="M21 19 C21 16.5, 23 15, 24.5 15 C26.5 15, 28 16.5, 27.5 18.5 C26.8 21, 24.5 22, 24.5 24.5 M24.5 28.5 V29" stroke="#FFFFFF" stroke-width="2.5" stroke-linecap="round" fill="none"/>
    `
  }
};

// Attack type color scheme (Vibrant military & intelligence style)
export const GTD_ATTACK_COLORS: Record<string, string> = {
  'Bombing/Explosion': '#EF4444',            // Red
  'Armed Assault': '#F97316',                // Orange
  'Hostage Taking (Kidnapping)': '#A855F7',  // Purple
  'Facility/Infrastructure Attack': '#06B6D4',// Cyan
  'Assassination': '#E11D48',                // Rose
  'Hostage Taking (Barricade Incident)': '#EC4899', // Pink
  'Unarmed Assault': '#EAB308',              // Amber
  'Hijacking': '#3B82F6',                    // Blue
  'Unknown': '#94A3B8',                      // Slate
};

// Tactical SVG Badge Cache for Cesium GPU Billboards & UI
const tacticalSvgCache = new Map<string, string>();

export function getTacticalSvgDataUrl(attackType: string): string {
  if (tacticalSvgCache.has(attackType)) {
    return tacticalSvgCache.get(attackType)!;
  }

  const item = TACTICAL_SVG_ICONS[attackType] || TACTICAL_SVG_ICONS['Unknown'];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
    <circle cx="24" cy="24" r="22" fill="#0B0F19"/>
    <circle cx="24" cy="24" r="20.5" fill="none" stroke="${item.color}" stroke-width="2.5"/>
    ${item.svgInner}
  </svg>`.replace(/\s+/g, ' ').trim();

  const dataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  tacticalSvgCache.set(attackType, dataUrl);
  return dataUrl;
}

// State coordinates mapping for camera fly-to
export const NIGERIA_STATE_COORDS: Record<string, { lat: number; lon: number; height: number }> = {
  'Borno': { lat: 11.8333, lon: 13.1500, height: 280000 },
  'Benue': { lat: 7.3333, lon: 8.7500, height: 220000 },
  'Plateau': { lat: 9.2167, lon: 9.5167, height: 200000 },
  'Yobe': { lat: 12.0000, lon: 11.5000, height: 250000 },
  'Kaduna': { lat: 10.5167, lon: 7.4333, height: 220000 },
  'Adamawa': { lat: 9.3333, lon: 12.5000, height: 240000 },
  'Delta': { lat: 5.5000, lon: 6.0000, height: 200000 },
  'Rivers': { lat: 4.8167, lon: 7.0000, height: 180000 },
  'Kano': { lat: 12.0000, lon: 8.5167, height: 200000 },
  'Taraba': { lat: 7.8667, lon: 9.7833, height: 240000 },
  'Lagos': { lat: 6.5244, lon: 3.3792, height: 140000 },
  'Federal Capital Territory': { lat: 9.0765, lon: 7.3986, height: 140000 },
  'Abuja': { lat: 9.0765, lon: 7.3986, height: 140000 },
  'Zamfara': { lat: 12.1667, lon: 6.6667, height: 220000 },
  'Katsina': { lat: 12.9833, lon: 7.6000, height: 220000 },
  'Sokoto': { lat: 13.0667, lon: 5.2333, height: 200000 },
  'Niger': { lat: 10.0000, lon: 6.0000, height: 260000 },
  'Bauchi': { lat: 10.3167, lon: 9.8433, height: 220000 },
  'Gombe': { lat: 10.2833, lon: 11.1667, height: 180000 },
  'Bayelsa': { lat: 4.7500, lon: 6.0833, height: 160000 },
  'Akwa Ibom': { lat: 5.0000, lon: 7.8333, height: 160000 },
  'Cross River': { lat: 5.8667, lon: 8.5833, height: 200000 },
  'Ogun': { lat: 7.0000, lon: 3.5833, height: 180000 },
  'Oyo': { lat: 8.0000, lon: 4.0000, height: 200000 },
  'Osun': { lat: 7.5000, lon: 4.5000, height: 160000 },
  'Ondo': { lat: 7.1000, lon: 5.1500, height: 180000 },
  'Ekiti': { lat: 7.6667, lon: 5.2500, height: 160000 },
  'Edo': { lat: 6.5000, lon: 6.0000, height: 180000 },
  'Enugu': { lat: 6.5000, lon: 7.5000, height: 160000 },
  'Anambra': { lat: 6.2000, lon: 7.0000, height: 150000 },
  'Imo': { lat: 5.4833, lon: 7.0333, height: 150000 },
  'Abia': { lat: 5.4167, lon: 7.5000, height: 150000 },
  'Ebonyi': { lat: 6.2500, lon: 8.0833, height: 160000 },
  'Kogi': { lat: 7.8000, lon: 6.7333, height: 200000 },
  'Kwara': { lat: 8.5000, lon: 4.5500, height: 200000 },
  'Nassarawa': { lat: 8.5000, lon: 8.5000, height: 200000 },
  'Kebbi': { lat: 11.5000, lon: 4.0000, height: 220000 },
  'Jigawa': { lat: 12.2500, lon: 9.7500, height: 200000 },
};

@Injectable({
  providedIn: 'root'
})
export class GtdGlobeService {
  private viewer?: Cesium.Viewer;
  private gtdDataSource?: Cesium.CustomDataSource;
  private billboardCollection?: Cesium.BillboardCollection;
  private cylinderEntities: Cesium.Entity[] = [];

  // Reactive state
  private allIncidents: GtdIncident[] = [];
  private incidentMap = new Map<string, GtdIncident>();

  private statsSubject = new BehaviorSubject<GtdStats>({
    totalIncidents: 0,
    totalKilled: 0,
    totalWounded: 0,
    totalHostages: 0,
    filteredIncidents: 0,
    filteredKilled: 0,
    filteredWounded: 0,
    filteredHostages: 0,
  });
  public stats$: Observable<GtdStats> = this.statsSubject.asObservable();

  private attackTypesSubject = new BehaviorSubject<GtdAttackTypeCount[]>([]);
  public attackTypes$: Observable<GtdAttackTypeCount[]> = this.attackTypesSubject.asObservable();

  private stateHotspotsSubject = new BehaviorSubject<GtdStateHotspot[]>([]);
  public stateHotspots$: Observable<GtdStateHotspot[]> = this.stateHotspotsSubject.asObservable();

  private topGroupsSubject = new BehaviorSubject<GtdGroupCount[]>([]);
  public topGroups$: Observable<GtdGroupCount[]> = this.topGroupsSubject.asObservable();

  private isLayerVisibleSubject = new BehaviorSubject<boolean>(false);
  public isLayerVisible$: Observable<boolean> = this.isLayerVisibleSubject.asObservable();

  private is3dCylindersSubject = new BehaviorSubject<boolean>(true);
  public is3dCylinders$: Observable<boolean> = this.is3dCylindersSubject.asObservable();

  private selectedIncidentSubject = new BehaviorSubject<GtdIncident | null>(null);
  public selectedIncident$: Observable<GtdIncident | null> = this.selectedIncidentSubject.asObservable();

  private hoverTooltipSubject = new BehaviorSubject<GtdHoverTooltipInfo | null>(null);
  public hoverTooltip$: Observable<GtdHoverTooltipInfo | null> = this.hoverTooltipSubject.asObservable();

  private isPanelOpenSubject = new BehaviorSubject<boolean>(false);
  public isPanelOpen$: Observable<boolean> = this.isPanelOpenSubject.asObservable();

  private filterResetSubject = new Subject<void>();
  public filterReset$: Observable<void> = this.filterResetSubject.asObservable();

  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  public isLoading$: Observable<boolean> = this.isLoadingSubject.asObservable();

  public isLayerVisible(): boolean {
    return this.isLayerVisibleSubject.getValue();
  }

  // Current filter state
  public filterState: GtdFilterState = {
    minYear: 1976,
    maxYear: 2021,
    selectedAttackTypes: new Set<string>(),
    selectedGroup: 'ALL',
    selectedState: 'ALL',
    minCasualties: 0,
    searchQuery: '',
    onlyFatal: false,
    onlyKidnappings: false,
  };

  private isInitialized = false;
  private pointerMoveHandler?: Cesium.ScreenSpaceEventHandler;
  private clickHandler?: Cesium.ScreenSpaceEventHandler;

  constructor(
    private http: HttpClient,
    private ngZone: NgZone,
    private cameraService: CameraService
  ) {}

  /**
   * Initialize GTD Intelligence layer on Cesium Viewer
   */
  async init(viewer: Cesium.Viewer): Promise<void> {
    this.viewer = viewer;

    if (this.pointerMoveHandler) {
      try { this.pointerMoveHandler.destroy(); } catch (e) {}
      this.pointerMoveHandler = undefined;
    }
    if (this.clickHandler) {
      try { this.clickHandler.destroy(); } catch (e) {}
      this.clickHandler = undefined;
    }

    if (this.gtdDataSource) {
      try {
        if (this.viewer?.dataSources?.contains(this.gtdDataSource)) {
          this.viewer.dataSources.remove(this.gtdDataSource, true);
        }
      } catch (e) {}
    }

    this.gtdDataSource = new Cesium.CustomDataSource('GTD_INCIDENTS_DATA_SOURCE');
    await this.viewer.dataSources.add(this.gtdDataSource);

    if (this.billboardCollection) {
      try {
        this.viewer?.scene?.primitives?.remove(this.billboardCollection);
      } catch (e) {}
    }

    this.billboardCollection = new Cesium.BillboardCollection({
      blendOption: Cesium.BlendOption.OPAQUE_AND_TRANSLUCENT
    });
    this.viewer.scene.primitives.add(this.billboardCollection);

    this.setupMouseEvents();

    if (this.allIncidents.length === 0) {
      this.isLoadingSubject.next(true);
      await this.loadGtdData();
      this.isLoadingSubject.next(false);
    }

    this.applyFiltersAndRender();
    this.isInitialized = true;
    this.setLayerVisibility(this.isLayerVisibleSubject.getValue());
  }

  /**
   * Load GTD dataset directly from API (BaseURL + GlobalTerrorismNigeria)
   */
  private async loadGtdData(): Promise<void> {
    const settings = ConfigurationManagerService.settings;
    const url = `${settings.BaseURL}${settings.GlobalTerrorismNigeria}`;
    const token = getAuthToken();
    let headers = new HttpHeaders({
      'Accept': 'application/json'
    });
    if (token) {
      headers = headers.set('Authorization', token.startsWith('Bearer ') ? token : `Bearer ${token}`);
    }

    try {
      const response = await firstValueFrom(
        this.http.get<any[]>(url, { headers })
      );
      if (Array.isArray(response) && response.length > 0) {
        this.parseGtdJson(response);
      }
    } catch (error) {
      console.error('[GtdGlobeService] Error loading GTD dataset from API:', error);
    }
  }

  /**
   * Parse GTD JSON array from API response
   */
  private parseGtdJson(items: any[]): void {
    const incidents: GtdIncident[] = [];
    const attackTypeMap = new Map<string, number>();
    const groupMap = new Map<string, { count: number; killed: number }>();
    const stateMap = new Map<string, { count: number; killed: number; groups: Map<string, number>; lats: number[]; lons: number[] }>();

    let totalKilled = 0;
    let totalWounded = 0;
    let totalHostages = 0;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const lat = typeof item.latitude === 'number' ? item.latitude : parseFloat(item.latitude);
      const lon = typeof item.longitude === 'number' ? item.longitude : parseFloat(item.longitude);

      if (isNaN(lat) || isNaN(lon)) continue;

      const year = parseInt(item.iyear, 10) || 0;
      const month = parseInt(item.imonth, 10) || 0;
      const day = parseInt(item.iday, 10) || 0;

      let dateStr = `${year}`;
      if (month > 0) {
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        dateStr = `${monthNames[month - 1]} ${day > 0 ? day + ', ' : ''}${year}`;
      }

      const rawAttack = (item.attacktype1_txt || item.attacktype || 'Unknown').toString().trim();
      const attackType = rawAttack.length > 0 ? rawAttack : 'Unknown';
      const rawGname = (item.gname || 'Unknown').toString().trim();
      const gname = rawGname.length > 0 ? rawGname : 'Unknown';
      const rawState = (item.provstate || 'Unknown').toString().trim();
      const state = rawState.length > 0 ? rawState : 'Unknown';

      const nkill = Math.max(0, parseFloat(item.nkill) || 0);
      const nwound = Math.max(0, parseFloat(item.nwound) || 0);
      const nhostkid = Math.max(0, parseFloat(item.nhostkid) || 0);
      const isHostKid = item.ishostkid === 1 || item.ishostkid === '1' || item.ishostkid === true || nhostkid > 0;

      totalKilled += nkill;
      totalWounded += nwound;
      totalHostages += nhostkid;

      const color = GTD_ATTACK_COLORS[attackType] || '#94A3B8';
      const iconUrl = getTacticalSvgDataUrl(attackType);
      const icon = TACTICAL_SVG_ICONS[attackType]?.label || attackType;

      const incident: GtdIncident = {
        eventid: (item.eventid != null ? String(item.eventid) : `gtd-${i}`).trim(),
        iyear: year,
        imonth: month,
        iday: day,
        dateStr,
        country: (item.country_txt || item.country || 'Nigeria').toString().trim(),
        provstate: state,
        city: (item.city || 'Unknown').toString().trim(),
        latitude: lat,
        longitude: lon,
        specificity: parseInt(item.specificity, 10) || 1,
        vicinity: parseInt(item.vicinity, 10) || 0,
        location: (item.location || '').toString().trim(),
        summary: (item.summary || '').toString().trim(),
        success: item.success === 1 || item.success === '1' || item.success === true,
        suicide: item.suicide === 1 || item.suicide === '1' || item.suicide === true,
        attacktype: attackType,
        attacktype2: item.attacktype2_txt ? String(item.attacktype2_txt).trim() : (item.attacktype2 ? String(item.attacktype2).trim() : undefined),
        targtype: (item.targtype1_txt || item.targtype || 'Private Citizens & Property').toString().trim(),
        targsubtype: item.targsubtype1_txt ? String(item.targsubtype1_txt).trim() : (item.targsubtype ? String(item.targsubtype).trim() : undefined),
        corp: item.corp1 ? String(item.corp1).trim() : (item.corp ? String(item.corp).trim() : undefined),
        target: (item.target1 || item.target || '').toString().trim(),
        natlty: (item.natlty1_txt || item.natlty || '').toString().trim(),
        gname,
        gsubname: item.gsubname ? String(item.gsubname).trim() : undefined,
        motive: (item.motive || '').toString().trim(),
        claimed: item.claimed === 1 || item.claimed === '1' || item.claimed === true,
        claimmode: item.claimmode_txt ? String(item.claimmode_txt).trim() : (item.claimmode ? String(item.claimmode).trim() : undefined),
        weaptype: (item.weaptype1_txt || item.weaptype || 'Unknown').toString().trim(),
        weapsubtype: item.weapsubtype1_txt ? String(item.weapsubtype1_txt).trim() : (item.weapsubtype ? String(item.weapsubtype).trim() : undefined),
        weapdetail: (item.weapdetail || '').toString().trim(),
        nkill,
        nkillus: parseFloat(item.nkillus) || 0,
        nkillter: parseFloat(item.nkillter) || 0,
        nwound,
        nwoundus: parseFloat(item.nwoundus) || 0,
        property: parseInt(item.property, 10) || 0,
        ishostkid: isHostKid,
        nhostkid,
        ransom: item.ransom === 1 || item.ransom === '1' || item.ransom === true,
        ransomamt: item.ransomamt != null && item.ransomamt !== '' ? String(item.ransomamt).trim() : undefined,
        ransompaid: item.ransompaid != null && item.ransompaid !== '' ? String(item.ransompaid).trim() : undefined,
        hostkidoutcome: item.hostkidoutcome_txt ? String(item.hostkidoutcome_txt).trim() : (item.hostkidoutcome ? String(item.hostkidoutcome).trim() : undefined),
        scite1: (item.scite1 || '').toString().trim(),
        scite2: (item.scite2 || '').toString().trim(),
        scite3: (item.scite3 || '').toString().trim(),
        dbsource: (item.dbsource || 'START GTD').toString().trim(),
        color,
        icon,
        iconUrl,
        totalCasualties: nkill + nwound,
      };

      incidents.push(incident);
      this.incidentMap.set(incident.eventid, incident);

      // Attack types counter
      attackTypeMap.set(attackType, (attackTypeMap.get(attackType) || 0) + 1);

      // Groups counter
      const gEntry = groupMap.get(gname) || { count: 0, killed: 0 };
      gEntry.count++;
      gEntry.killed += nkill;
      groupMap.set(gname, gEntry);

      // State counter
      if (!stateMap.has(state)) {
        stateMap.set(state, {
          count: 0,
          killed: 0,
          groups: new Map<string, number>(),
          lats: [],
          lons: []
        });
      }
      const sEntry = stateMap.get(state)!;
      sEntry.count++;
      sEntry.killed += nkill;
      sEntry.lats.push(lat);
      sEntry.lons.push(lon);
      sEntry.groups.set(gname, (sEntry.groups.get(gname) || 0) + 1);
    }

    this.finalizeIncidentsAggregation(incidents, attackTypeMap, groupMap, stateMap, totalKilled, totalWounded, totalHostages);
  }



  /**
   * Finalize GTD aggregation maps and subjects
   */
  private finalizeIncidentsAggregation(
    incidents: GtdIncident[],
    attackTypeMap: Map<string, number>,
    groupMap: Map<string, { count: number; killed: number }>,
    stateMap: Map<string, { count: number; killed: number; groups: Map<string, number>; lats: number[]; lons: number[] }>,
    totalKilled: number,
    totalWounded: number,
    totalHostages: number
  ): void {
    this.allIncidents = incidents;

    // Attack types list
    const attackTypeList: GtdAttackTypeCount[] = Array.from(attackTypeMap.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({
        name,
        color: GTD_ATTACK_COLORS[name] || '#94A3B8',
        icon: TACTICAL_SVG_ICONS[name]?.label || name,
        iconUrl: getTacticalSvgDataUrl(name),
        count,
        selected: true,
      }));
    this.attackTypesSubject.next(attackTypeList);

    // Top groups list
    const topGroupsList: GtdGroupCount[] = Array.from(groupMap.entries())
      .filter(([name]) => name !== 'Unknown')
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 15)
      .map(([name, data]) => ({
        name,
        count: data.count,
        killed: data.killed,
      }));
    // Also prepend 'Unknown' and 'ALL' in UI
    this.topGroupsSubject.next(topGroupsList);

    // State hotspots list
    const stateHotspotsList: GtdStateHotspot[] = Array.from(stateMap.entries())
      .filter(([st]) => st !== 'Unknown' && st.trim().length > 0)
      .sort((a, b) => b[1].count - a[1].count)
      .map(([stateName, data]) => {
        let topG = 'Unknown';
        let maxGCount = 0;
        data.groups.forEach((cnt, grp) => {
          if (cnt > maxGCount) {
            maxGCount = cnt;
            topG = grp;
          }
        });

        const avgLat = data.lats.reduce((a, b) => a + b, 0) / data.lats.length;
        const avgLon = data.lons.reduce((a, b) => a + b, 0) / data.lons.length;

        return {
          state: stateName,
          count: data.count,
          totalKilled: data.killed,
          topGroup: topG,
          latitude: avgLat,
          longitude: avgLon,
        };
      });
    this.stateHotspotsSubject.next(stateHotspotsList);

    // Initial stats
    this.statsSubject.next({
      totalIncidents: incidents.length,
      totalKilled,
      totalWounded,
      totalHostages,
      filteredIncidents: incidents.length,
      filteredKilled: totalKilled,
      filteredWounded: totalWounded,
      filteredHostages: totalHostages,
    });
  }

  /**
   * Filter and render incidents on the Cesium Globe
   */
  public applyFiltersAndRender(): void {
    if (!this.viewer || !this.billboardCollection) return;

    const { minYear, maxYear, selectedAttackTypes, selectedGroup, selectedState, minCasualties, searchQuery, onlyFatal, onlyKidnappings } = this.filterState;

    const query = searchQuery.trim().toLowerCase();

    // Filter incidents
    const filtered = this.allIncidents.filter(inc => {
      if (inc.iyear < minYear || inc.iyear > maxYear) return false;
      if (selectedAttackTypes.size > 0 && !selectedAttackTypes.has(inc.attacktype)) return false;
      if (selectedGroup !== 'ALL' && inc.gname !== selectedGroup) return false;
      if (selectedState !== 'ALL' && inc.provstate !== selectedState) return false;
      if (minCasualties > 0 && inc.totalCasualties < minCasualties) return false;
      if (onlyFatal && inc.nkill <= 0) return false;
      if (onlyKidnappings && !inc.ishostkid) return false;

      if (query.length > 0) {
        const matchText = `${inc.city} ${inc.provstate} ${inc.gname} ${inc.target} ${inc.attacktype} ${inc.summary} ${inc.eventid}`.toLowerCase();
        if (!matchText.includes(query)) return false;
      }

      return true;
    });

    // Update stats
    let fKilled = 0;
    let fWounded = 0;
    let fHostages = 0;

    for (const inc of filtered) {
      fKilled += inc.nkill;
      fWounded += inc.nwound;
      fHostages += inc.nhostkid;
    }

    const currentStats = this.statsSubject.getValue();
    this.statsSubject.next({
      ...currentStats,
      filteredIncidents: filtered.length,
      filteredKilled: fKilled,
      filteredWounded: fWounded,
      filteredHostages: fHostages,
    });

    // Render on Cesium
    this.renderIncidents(filtered);
  }

  /**
   * Render Billboards with Incident Icons & High-Impact 3D Cylinders
   */
  private renderIncidents(incidents: GtdIncident[]): void {
    if (!this.billboardCollection || !this.viewer || !this.gtdDataSource) return;

    // Clear existing
    this.billboardCollection.removeAll();
    this.gtdDataSource.entities.removeAll();
    this.cylinderEntities = [];

    const isVisible = this.isLayerVisibleSubject.getValue();
    this.billboardCollection.show = isVisible;
    this.gtdDataSource.show = isVisible;

    if (!isVisible) {
      this.viewer.scene.requestRender();
      return;
    }

    const show3dCylinders = this.is3dCylindersSubject.getValue();

    for (let i = 0; i < incidents.length; i++) {
      const inc = incidents[i];
      const pos = Cesium.Cartesian3.fromDegrees(inc.longitude, inc.latitude, 10);
      const imageSrc = inc.iconUrl;

      // Base scale calculated by casualty severity
      let scale = 0.52;
      if (inc.nkill >= 50) scale = 0.85;
      else if (inc.nkill >= 20) scale = 0.72;
      else if (inc.nkill >= 5) scale = 0.62;
      else if (inc.nkill >= 1) scale = 0.56;

      // Add high-performance GPU Billboard with tactical vector badge
      this.billboardCollection.add({
        position: pos,
        image: imageSrc,
        scale,
        id: inc.eventid,
        distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, 18000000),
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        verticalOrigin: Cesium.VerticalOrigin.CENTER,
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
      });

      // For catastrophic mass casualty incidents (nkill >= 25), add a 3D glowing pillar/cylinder
      if (show3dCylinders && inc.nkill >= 25) {
        const cylinderHeight = Math.min(250000, inc.nkill * 1800);
        const halfHeight = cylinderHeight / 2;
        const color = Cesium.Color.fromCssColorString(inc.color || '#EF4444');

        const cylinderEntity = this.gtdDataSource.entities.add({
          id: `cyl-${inc.eventid}`,
          position: Cesium.Cartesian3.fromDegrees(inc.longitude, inc.latitude, halfHeight),
          cylinder: {
            length: cylinderHeight,
            topRadius: 1800.0,
            bottomRadius: 3500.0,
            material: color.withAlpha(0.65),
            outline: true,
            outlineColor: Cesium.Color.WHITE.withAlpha(0.9),
            outlineWidth: 2,
          },
        });
        (cylinderEntity as any).gtdIncident = inc;
        this.cylinderEntities.push(cylinderEntity);
      }
    }

    this.viewer.scene.requestRender();
  }

  /**
   * Setup ScreenSpace mouse handlers for hover tooltips and left-click dossier opening
   */
  private setupMouseEvents(): void {
    if (!this.viewer) return;

    const scene = this.viewer.scene;

    // Mouse Move for Hover Tooltip
    this.pointerMoveHandler = new Cesium.ScreenSpaceEventHandler(scene.canvas);
    this.pointerMoveHandler.setInputAction((movement: any) => {
      if (!this.isLayerVisibleSubject.getValue()) {
        if (this.hoverTooltipSubject.getValue()) {
          this.hoverTooltipSubject.next(null);
        }
        return;
      }

      const pickedObject = scene.pick(movement.endPosition);
      if (Cesium.defined(pickedObject) && pickedObject.id) {
        let incidentId: string | undefined;

        if (typeof pickedObject.id === 'string') {
          incidentId = pickedObject.id.startsWith('cyl-')
            ? pickedObject.id.replace('cyl-', '')
            : pickedObject.id;
        } else if (pickedObject.id.gtdIncident) {
          incidentId = pickedObject.id.gtdIncident.eventid;
        }

        if (incidentId && this.incidentMap.has(incidentId)) {
          const incident = this.incidentMap.get(incidentId)!;
          this.ngZone.run(() => {
            this.hoverTooltipSubject.next({
              x: movement.endPosition.x,
              y: movement.endPosition.y,
              incident,
            });
          });
          return;
        }
      }

      if (this.hoverTooltipSubject.getValue()) {
        this.ngZone.run(() => {
          this.hoverTooltipSubject.next(null);
        });
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    // Left Click for Modal Dossier
    this.clickHandler = new Cesium.ScreenSpaceEventHandler(scene.canvas);
    this.clickHandler.setInputAction((click: any) => {
      if (!this.isLayerVisibleSubject.getValue()) return;

      const pickedObject = scene.pick(click.position);
      if (Cesium.defined(pickedObject) && pickedObject.id) {
        let incidentId: string | undefined;

        if (typeof pickedObject.id === 'string') {
          incidentId = pickedObject.id.startsWith('cyl-')
            ? pickedObject.id.replace('cyl-', '')
            : pickedObject.id;
        } else if (pickedObject.id.gtdIncident) {
          incidentId = pickedObject.id.gtdIncident.eventid;
        }

        if (incidentId && this.incidentMap.has(incidentId)) {
          const incident = this.incidentMap.get(incidentId)!;
          this.ngZone.run(() => {
            this.selectedIncidentSubject.next(incident);
          });
        }
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
  }

  /**
   * Toggle Layer Visibility
   */
  public toggleLayer(force?: boolean): void {
    const nextVal = force !== undefined ? force : !this.isLayerVisibleSubject.getValue();
    this.setLayerVisibility(nextVal);
    if (!nextVal) {
      this.setPanelOpen(false);
    }
  }

  public setLayerVisibility(visible: boolean): void {
    this.isLayerVisibleSubject.next(visible);
    if (this.billboardCollection) {
      this.billboardCollection.show = visible;
    }
    if (this.gtdDataSource) {
      this.gtdDataSource.show = visible;
    }
    if (!visible) {
      this.hoverTooltipSubject.next(null);
    } else {
      if (this.billboardCollection && this.billboardCollection.length === 0 && this.allIncidents.length > 0) {
        this.applyFiltersAndRender();
      }
    }
    if (this.viewer) {
      this.viewer.scene.requestRender();
    }
  }

  /**
   * Toggle 3D Casualty Cylinders
   */
  public toggle3dCylinders(): void {
    const next = !this.is3dCylindersSubject.getValue();
    this.is3dCylindersSubject.next(next);
    this.applyFiltersAndRender();
  }

  /**
   * Toggle Control Panel visibility
   */
  public togglePanel(): void {
    this.isPanelOpenSubject.next(!this.isPanelOpenSubject.getValue());
  }

  public setPanelOpen(open: boolean): void {
    this.isPanelOpenSubject.next(open);
  }

  /**
   * Select an incident for the detailed modal
   */
  public openIncidentModal(incident: GtdIncident): void {
    this.selectedIncidentSubject.next(incident);
  }

  public closeIncidentModal(): void {
    this.selectedIncidentSubject.next(null);
  }

  /**
   * Camera Navigation to Nigeria Overview
   */
  public flyToNigeria(duration: number = 1.8): void {
    if (!this.viewer) return;
        // Nigeria bounding rectangle with comfortable margins: [west, south, east, north]
        const nigeriaRectangle = Cesium.Rectangle.fromDegrees(2.2, 3.5, 15.2, 14.5);
        this.viewer.camera.flyTo({
          destination: nigeriaRectangle,
          orientation: {
            heading: Cesium.Math.toRadians(0.0),
            pitch: Cesium.Math.toRadians(-89.9),
            roll: 0.0,
          },
          duration,
        });
  }

  /**
   * Fly to a specific State Hotspot
   */
  public flyToState(stateName: string): void {
    if (!this.viewer) return;

    const stateIncidents = this.allIncidents.filter(
      i => i.provstate.toLowerCase().trim() === stateName.toLowerCase().trim()
    );
    const coords = NIGERIA_STATE_COORDS[stateName];

    if (stateIncidents.length > 0) {
      const positions: Cesium.Cartesian3[] = stateIncidents.map(inc =>
        Cesium.Cartesian3.fromDegrees(inc.longitude, inc.latitude)
      );
      if (coords) {
        positions.push(Cesium.Cartesian3.fromDegrees(coords.lon, coords.lat));
      }

      const boundingSphere = Cesium.BoundingSphere.fromPoints(positions);
      boundingSphere.radius = Math.max(boundingSphere.radius * 1.35, 60000);

      this.viewer.camera.flyToBoundingSphere(boundingSphere, {
        offset: new Cesium.HeadingPitchRange(
          Cesium.Math.toRadians(0.0),
          Cesium.Math.toRadians(-89.9),
          0
        ),
        duration: 1.5,
      });
    } else if (coords) {
      const target = Cesium.Cartesian3.fromDegrees(coords.lon, coords.lat);
      const boundingSphere = new Cesium.BoundingSphere(target, coords.height ? coords.height * 0.6 : 120000);
      this.viewer.camera.flyToBoundingSphere(boundingSphere, {
        offset: new Cesium.HeadingPitchRange(
          Cesium.Math.toRadians(0.0),
          Cesium.Math.toRadians(-89.9),
          0
        ),
        duration: 1.5,
      });
    }

    // Filter state
    this.filterState.selectedState = stateName;
    this.applyFiltersAndRender();
  }

  /**
   * Fly directly to an Incident's coordinates
   */
  public flyToIncident(incident: GtdIncident): void {
    if (!this.viewer) return;

    const target = Cesium.Cartesian3.fromDegrees(incident.longitude, incident.latitude);
    const boundingSphere = new Cesium.BoundingSphere(target, 25000);

    this.viewer.camera.flyToBoundingSphere(boundingSphere, {
      offset: new Cesium.HeadingPitchRange(
        Cesium.Math.toRadians(0.0),
        Cesium.Math.toRadians(-89.9),
        0
      ),
      duration: 1.5,
    });
  }

  /**
   * Filter helpers
   */
  public setYearRange(minYear: number, maxYear: number): void {
    this.filterState.minYear = minYear;
    this.filterState.maxYear = maxYear;
    this.applyFiltersAndRender();
  }

  public toggleAttackTypeFilter(type: string): void {
    if (this.filterState.selectedAttackTypes.has(type)) {
      this.filterState.selectedAttackTypes.delete(type);
    } else {
      this.filterState.selectedAttackTypes.add(type);
    }
    this.applyFiltersAndRender();
  }

  public selectGroupFilter(group: string): void {
    this.filterState.selectedGroup = group;
    this.applyFiltersAndRender();
  }

  public selectStateFilter(state: string): void {
    this.filterState.selectedState = state;
    this.applyFiltersAndRender();
  }

  public setSearchQuery(query: string): void {
    this.filterState.searchQuery = query;
    this.applyFiltersAndRender();
  }

  public toggleOnlyFatal(): void {
    this.filterState.onlyFatal = !this.filterState.onlyFatal;
    this.applyFiltersAndRender();
  }

  public toggleOnlyKidnappings(): void {
    this.filterState.onlyKidnappings = !this.filterState.onlyKidnappings;
    this.applyFiltersAndRender();
  }

  public resetAllFilters(): void {
    this.filterState = {
      minYear: 1976,
      maxYear: 2021,
      selectedAttackTypes: new Set<string>(),
      selectedGroup: 'ALL',
      selectedState: 'ALL',
      minCasualties: 0,
      searchQuery: '',
      onlyFatal: false,
      onlyKidnappings: false,
    };
    const currentAttackTypes = this.attackTypesSubject.getValue().map(item => ({
      ...item,
      selected: true
    }));
    this.attackTypesSubject.next(currentAttackTypes);
    this.filterResetSubject.next();
    this.applyFiltersAndRender();
  }

  public destroy(): void {
    if (this.pointerMoveHandler && !this.pointerMoveHandler.isDestroyed()) {
      try { this.pointerMoveHandler.destroy(); } catch (e) {}
    }
    this.pointerMoveHandler = undefined;
    if (this.clickHandler && !this.clickHandler.isDestroyed()) {
      try { this.clickHandler.destroy(); } catch (e) {}
    }
    this.clickHandler = undefined;
    if (this.billboardCollection && this.viewer) {
      try {
        this.viewer.scene.primitives.remove(this.billboardCollection);
      } catch (e) {}
    }
    this.billboardCollection = undefined;
    if (this.gtdDataSource && this.viewer) {
      try {
        if (this.viewer.dataSources.contains(this.gtdDataSource)) {
          this.viewer.dataSources.remove(this.gtdDataSource, true);
        }
      } catch (e) {}
    }
    this.gtdDataSource = undefined;
    this.viewer = undefined;
    this.isInitialized = false;
  }
}
